/**
 * @file src/api/progression.js
 * @description API client for the progression service. Backend was built
 *   in the userFlowCorrection branch; every endpoint listed here exists
 *   server-side and has been smoke-tested end-to-end. UI consumption is
 *   the gap this client closes.
 */
import api from './client';

class ProgressionAPI {
  // ── Ladder reference (public-ish) ───────────────────────────────
  static async ladders()                  { return (await api.get('/progression/ladders')).data.data; }
  static async ladder(ref)                { return (await api.get(`/progression/ladders/${encodeURIComponent(ref)}`)).data.data; }

  // ── My state ────────────────────────────────────────────────────
  static async me()                       { return (await api.get('/progression/me')).data.data; }
  static async myTrack(trackKey)          { return (await api.get(`/progression/me/tracks/${encodeURIComponent(trackKey)}`)).data.data; }
  static async myHistory(params = {})     { return (await api.get('/progression/me/history', { params })).data.data; }
  static async earnGuide()                { return (await api.get('/progression/me/earn')).data.data; }
  // Time on Moksy: one heartbeat a minute, only when the minute had an interaction.
  static async activeMinute(interactions = 1) { return (await api.post('/progression/me/active-minute', { interactions })).data.data; }
  // A visitor on a shared link (no session): 'land' on arrival, 'stay' a minute later with the interactions seen.
  static visitorId() {
    // One id per browser, so a landing and its stay ping are the same
    // visitor even when the network address changes between them.
    try {
      const k = 'moksy_visitor';
      let v = localStorage.getItem(k);
      if (!v) { v = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`); localStorage.setItem(k, v); }
      return v;
    } catch { return null; }
  }
  static async referralVisit(code, stage = 'land', interactions = 0) { return (await api.post(`/progression/referrals/${encodeURIComponent(code)}/visit`, { stage, interactions, visitorId: ProgressionAPI.visitorId() })).data.data; }
  static async leaderboard(trackKey, params = {}) { return (await api.get(`/progression/leaderboard/${encodeURIComponent(trackKey)}`, { params })).data.data; }

  // ── Track lifecycle ─────────────────────────────────────────────
  static async applyForTrack(payload = {}) { return (await api.post('/progression/tracks/apply', payload)).data.data; }

  // ── Connections (network_worth source) ──────────────────────────
  static async listConnections()          { return (await api.get('/progression/me/connections')).data.data; }
  static async addConnection(connectedAccountId) { return (await api.post('/progression/me/connections', { connectedAccountId })).data.data; }
  static async removeConnection(connectedAccountId) { return (await api.delete(`/progression/me/connections/${encodeURIComponent(connectedAccountId)}`)).data.data; }

  // ── Endorsements ────────────────────────────────────────────────
  static async endorse(payload = {})      { return (await api.post('/progression/me/endorse', payload)).data.data; }

  // ── Mentorship ──────────────────────────────────────────────────
  static async declareMentor(payload = {}){ return (await api.post('/progression/me/mentor', payload)).data.data; }
  static async endMentor(payload = {})    { return (await api.delete('/progression/me/mentor', { data: payload })).data.data; }
  static async listMyMentees(params = {}) { return (await api.get('/progression/me/mentees', { params })).data.data; }

  // ── Critiques (designer track) ──────────────────────────────────
  static async submitCritique(payload = {}){ return (await api.post('/progression/critiques', payload)).data.data; }
  static async listMyCritiques(params = {}){ return (await api.get('/progression/me/critiques', { params })).data.data; }

  // ── A11y audits (designer track) ────────────────────────────────
  static async submitA11yAudit(payload = {}){ return (await api.post('/progression/a11y-audits', payload)).data.data; }
  static async listMyA11yAudits(params = {}){ return (await api.get('/progression/me/a11y-audits', { params })).data.data; }

  // ── Exam results (multi-track) ──────────────────────────────────
  static async submitExamResult(payload = {}){ return (await api.post('/progression/exam-results', payload)).data.data; }
  static async listMyExamResults(params = {}){ return (await api.get('/progression/me/exam-results', { params })).data.data; }

  // ── Referrals (customer track) ──────────────────────────────────
  static async issueReferralCode()         { return (await api.post('/progression/me/referrals')).data.data; }
  static async listMyReferrals(params = {}){ return (await api.get('/progression/me/referrals', { params })).data.data; }

  // ── Beta program (customer track) ───────────────────────────────
  static async listMyBetaInvites(params = {}){ return (await api.get('/progression/me/beta-invites', { params })).data.data; }

  static async adminApproveTrack(payload = {}) { return (await api.post('/admin/progression/tracks/approve', payload)).data.data; }
  static async adminSuspendTrack(payload = {}) { return (await api.post('/admin/progression/tracks/suspend', payload)).data.data; }
  static async adminPortfolioReview(payload = {}) { return (await api.post('/admin/progression/portfolio-review', payload)).data.data; }

  static async adminInviteToBeta(payload = {}) { return (await api.post('/admin/progression/beta-invites', payload)).data.data; }
  static async adminListBetaInvites(params = {}) { return (await api.get('/admin/progression/beta-invites', { params })).data.data; }
  static async adminIssueStrike(payload = {}) { return (await api.post('/admin/progression/strikes', payload)).data.data; }
  static async adminVoidStrike(id, payload = {}) { return (await api.post(`/admin/progression/strikes/${encodeURIComponent(id)}/void`, payload)).data.data; }

  // ── MOX wallet (lives in subscriptions, surfaced via gateway) ───
  static async myMoxWallet()              { return (await api.get('/my-subscription/mox-wallet')).data.data; }
  static async convertMoxToPillars(payload = {}) { return (await api.post('/mox/convert-to-pillars', payload)).data.data; }
  static async withdrawMox(payload = {})  { return (await api.post('/mox/withdraw', payload)).data.data; }
  static async listMoxWithdrawals(params = {}) { return (await api.get('/mox/withdrawals', { params })).data.data; }

  // ── Reviewer eligibility matrix (priority #2) ───────────────────
  // Read endpoints are open to any authenticated user. Writes require
  // owner-group + the `progression.eligibility.manage` scope, enforced
  // at the gateway.
  static async getEligibilityMatrix() {
    return (await api.get('/progression/eligibility-matrix')).data.data;
  }
  static async checkEligibility({ track, level, tier }) {
    return (await api.get('/progression/eligibility/check', {
      params: { track, level, tier }
    })).data.data;
  }
  static async adminPatchEligibilityCell(track, tier, payload = {}) {
    return (await api.patch(
      `/admin/progression/eligibility-matrix/cells/${encodeURIComponent(track)}/${encodeURIComponent(tier)}`,
      payload
    )).data.data;
  }
}

export default ProgressionAPI;
